"use client";

import { className as classNameHelper } from "@/composites/foundation/utilities/className";
import {
	Accordion,
	Button,
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Text
} from "@pamperedchef/design-system";
import React from "react";

import * as styles from "./Footer.module.scss";

export type MobileDesktopVisibility = "Both" | "Mobile Only" | "Desktop Only";

type ExtendedLinkProps<LinkComponentProps> = LinkProps<LinkComponentProps> & {
	mobile_desktop_visibility?: MobileDesktopVisibility;
};

export interface FooterProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	shopNav: {
		title: string | React.JSX.Element | LinkProps<LinkComponentProps>;
		mobile_desktop_visibility?: MobileDesktopVisibility;
		links?: ExtendedLinkProps<LinkComponentProps>[];
	}[][];
	logo: ImageProps<ImageComponentProps>;
	footerText: string;
	socialLinks: {
		link: LinkProps<LinkComponentProps>;
		icon: ImageProps<ImageComponentProps>;
	}[];
	newsletter: {
		title: string;
		description: string;
		cta: LinkProps<LinkComponentProps>;
		onCtaClick?: () => void;
	};
	mainNav: {
		title: string;
		mobile_desktop_visibility?: MobileDesktopVisibility;
		links?: ExtendedLinkProps<LinkComponentProps>[];
	}[];
	copyright: string;
	legalNav: LinkProps<LinkComponentProps>[];
}

/**
 * Footer component
 */
export function Footer<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>(props: FooterProps<LinkComponentProps, ImageComponentProps>): JSX.Element {
	return (
		<footer>
			<div className={styles["footer-top"]}>
				<Container>
					<Accordion
						// @ts-expect-error DS 4.0.85 dropped `animated` from AccordionProps. Runtime still accepts it.
						animated={true}
						className={styles["footer-accordion"]}
						/* DS 4.0.85 narrowed `sections.title` to `string | Element`.
						   Runtime still accepts the broader shape; cast below unblocks
						   `next build` until DS publishes a fix. */
						sections={
							props.shopNav
								.flat()
								.filter(
									(section) =>
										section.mobile_desktop_visibility ===
											"Both" ||
										section.mobile_desktop_visibility ===
											"Mobile Only" ||
										!section.mobile_desktop_visibility
								)
								.map((section) => ({
									title:
										typeof section.title !== "string" &&
										!React.isValidElement(section.title) ? (
											<Link<LinkComponentProps>
												{...(section.title as LinkProps<LinkComponentProps>)}
												kind="tertiary"
											/>
										) : (
											section.title
										),
									content:
										section.links &&
										section.links?.length > 0 ? (
											<ul>
												{section.links?.map(
													(
														{
															mobile_desktop_visibility,
															...link
														},
														index
													) => (
														<li key={index}>
															<Link<LinkComponentProps>
																{...(link as LinkProps<LinkComponentProps>)}
																kind="tertiary"
																{...classNameHelper(
																	mobile_desktop_visibility ===
																		"Mobile Only"
																		? styles[
																				"mobile-only"
																			]
																		: "",
																	mobile_desktop_visibility ===
																		"Desktop Only"
																		? styles[
																				"desktop-only"
																			]
																		: ""
																)}
															/>
														</li>
													)
												)}
											</ul>
										) : undefined
								})) as unknown as Parameters<
								typeof Accordion
							>[0]["sections"]
						}
					/>
					<div className={styles["footer-shopnav"]}>
						{props.shopNav.map((chunk, index) => (
							<div
								key={index}
								className={styles["footer-shopnav-section"]}
							>
								{chunk
									.filter(
										(section) =>
											section.mobile_desktop_visibility ===
												"Both" ||
											section.mobile_desktop_visibility ===
												"Desktop Only" ||
											!section.mobile_desktop_visibility
									)
									.map((section, idx) => (
										<div
											key={idx}
											className={
												styles["footer-shopnav-super"]
											}
										>
											<Heading
												tag="h2"
												className={
													styles[
														"footer-shopnav-title"
													]
												}
											>
												{typeof section.title !==
												"string" ? (
													<Link<LinkComponentProps>
														{...(section.title as LinkProps<LinkComponentProps>)}
														kind="tertiary"
													/>
												) : (
													section.title
												)}
											</Heading>
											{section.links && (
												<ul>
													{section.links?.map(
														(
															{
																mobile_desktop_visibility,
																...link
															},
															i
														) => (
															<li key={i}>
																<Link<LinkComponentProps>
																	{...(link as LinkProps<LinkComponentProps>)}
																	kind="tertiary"
																	{...classNameHelper(
																		mobile_desktop_visibility ===
																			"Mobile Only"
																			? styles[
																					"mobile-only"
																				]
																			: "",
																		mobile_desktop_visibility ===
																			"Desktop Only"
																			? styles[
																					"desktop-only"
																				]
																			: ""
																	)}
																/>
															</li>
														)
													)}
												</ul>
											)}
										</div>
									))}
							</div>
						))}
					</div>
				</Container>
			</div>
			<div className={styles["footer-middle"]}>
				<Container containerClassName={styles["footer-middle-inner"]}>
					<div className={styles["footer-logo-container"]}>
						<Image<ImageComponentProps>
							{...props.logo}
							className={styles["footer-logo"]}
							alt={props.logo.alt || "Footer Logo"}
						/>
						<Text className={styles["footer-text"]} styledAs="sml">
							{props.footerText}
						</Text>
					</div>
					{props.socialLinks && (
						<ul className={styles["social-icons"]}>
							{props.socialLinks?.map((social, index) => (
								<li key={index}>
									<Link<LinkComponentProps>
										{...social.link}
										kind="tertiary"
									>
										<Image<ImageComponentProps>
											{...social.icon}
											{...classNameHelper(
												styles["social-image"],
												social.icon.className
											)}
											alt={
												social.icon.alt || "Social Logo"
											}
										/>
									</Link>
								</li>
							))}
						</ul>
					)}
				</Container>
			</div>
			<div className={styles["footer-bottom"]}>
				<Container>
					<div className={styles["footer-bottom-nav"]}>
						<div className={styles["footer-newsletter"]}>
							<Heading tag="h2">{props.newsletter.title}</Heading>
							<Text styledAs="h2">
								{props.newsletter.description}
							</Text>
							{props.newsletter.onCtaClick ? (
								<Button
									kind="primary"
									size="lg"
									onPress={props.newsletter.onCtaClick}
								>
									{props.newsletter.cta.children}
								</Button>
							) : (
								<Link<LinkComponentProps>
									{...props.newsletter.cta}
									kind="primary"
									size="md"
								/>
							)}
						</div>
						<div className={styles["footer-mainnav"]}>
							{props.mainNav.map(
								(
									{ mobile_desktop_visibility, ...section },
									index
								) => (
									<div
										key={index}
										{...classNameHelper(
											mobile_desktop_visibility ===
												"Mobile Only"
												? styles["mobile-only"]
												: "",
											mobile_desktop_visibility ===
												"Desktop Only"
												? styles["desktop-only"]
												: "",
											styles["footer-mainnav-column"]
										)}
									>
										<Heading tag="h2">
											{section.title}
										</Heading>
										<ul>
											{section?.links?.map(
												(
													{
														mobile_desktop_visibility:
															visibility,
														...link
													},
													idx
												) => (
													<li key={idx}>
														<Link<LinkComponentProps>
															{...(link as LinkProps<LinkComponentProps>)}
															kind="tertiary"
															{...classNameHelper(
																visibility ===
																	"Mobile Only"
																	? styles[
																			"mobile-only"
																		]
																	: "",
																visibility ===
																	"Desktop Only"
																	? styles[
																			"desktop-only"
																		]
																	: ""
															)}
														/>
													</li>
												)
											)}
										</ul>
									</div>
								)
							)}
						</div>
					</div>
					<div className={styles["footer-legal"]}>
						<Text styledAs="xsr">{props.copyright}</Text>
						{props.legalNav && (
							<ul>
								{props.legalNav.map((link, index) => (
									<>
										{link.href !== undefined && (
											<li key={index}>
												<Link<LinkComponentProps>
													{...link}
													kind="tertiary"
												/>
											</li>
										)}
									</>
								))}
							</ul>
						)}
					</div>
				</Container>
			</div>
		</footer>
	);
}
